import React, { useState, useRef, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { v4 as uuidv4 } from 'uuid';

import { Input, CloseButton, BaseButton, ToolTip, useT } from '@repo/common';
import EmploymentTypeList from './EmploymentTypeList';
import LocationTypeList from './LocationTypeList';
import LocationInput from './LocationInput';
import MobileKeyAchievementBottomSheet from '../Experience/KeyAchievement/MobileKeyAchievementBottomSheet';

import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/24px/icon_24px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/24px/icon_24px_arrow-top.svg';
import { ReactComponent as InfoIcon } from '@repo/common/assets/icons/20px/icon_20px_info_outline.svg';
import { ReactComponent as TrophyIcon } from '@repo/common/assets/icons/20px/icon_20px_trophy-outline.svg';
import { ReactComponent as PlusIcon } from '@repo/common/assets/icons/20px/icon_20px_plus_outline.svg';
import { ReactComponent as DeleteIcon } from '@repo/common/assets/icons/24px/icon_24px_delete_outline.svg';
import { ReactComponent as DragAndDropIcon } from '@repo/common/assets/icons/16px/icon_16px_drag-and-drop.svg';

const EMPTY = {
  company: '',
  jobTitle: '',
  employmentType: '',
  employmentPeriod: '',
  location: '',
  locationType: '',
  headline: '',
  keyAchievements: [],
};

const KA_PLACEHOLDER = "Share result you're proud of.";

const REQUIRED = {
  company: 'Please add the company name',
  jobTitle: 'Please add a job title',
  employmentType: 'Please choose an employment type',
  employmentPeriod: 'Please add start and end dates',
};

const REQUIRED_KR = {
  company: '회사명을 입력해 주세요',
  jobTitle: '직무명을 입력해 주세요',
  employmentType: '고용 형태를 선택해 주세요',
  employmentPeriod: '시작일과 종료일을 입력해 주세요',
};

const getInputState = (open) => (open ? 'active' : 'default');
const getIconState = (value, open) => {
  if (value) return 'active';
  if (open) return 'focused';
  return 'default';
};

const MobileExperienceFullPage = ({ initialData, onClose, onSave }) => {
  const t = useT();
  const [form, setForm] = useState(() => ({
    ...EMPTY,
    ...(initialData || {}),
    keyAchievements: initialData?.keyAchievements || [],
  }));
  const [errors, setErrors] = useState({});
  const [openEmployment, setOpenEmployment] = useState(false);
  const [openLocationType, setOpenLocationType] = useState(false);
  const [locationFocused, setLocationFocused] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [kaSheetId, setKaSheetId] = useState(null); // 열린 KA row id (null=닫힘)

  const companyRef = useRef(null);
  const employmentRef = useRef(null);
  const locationTypeRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => companyRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    if (!openEmployment && !openLocationType) return;
    const handleClickOutside = (e) => {
      if (
        openEmployment &&
        employmentRef.current &&
        !employmentRef.current.contains(e.target)
      ) {
        setOpenEmployment(false);
      }
      if (
        openLocationType &&
        locationTypeRef.current &&
        !locationTypeRef.current.contains(e.target)
      ) {
        setOpenLocationType(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openEmployment, openLocationType]);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleAdd = () => {
    const nextErrors = {};
    Object.keys(REQUIRED).forEach((f) => {
      if (!String(form[f] ?? '').trim()) {
        nextErrors[f] = t(REQUIRED[f], REQUIRED_KR[f]);
      }
    });
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    onSave(form);
  };

  const addKaRow = () =>
    setForm((prev) => ({
      ...prev,
      keyAchievements: [
        ...prev.keyAchievements,
        { id: uuidv4(), title: '', description: '' },
      ],
    }));

  const openKaEdit = (id) => setKaSheetId(id);
  const closeKa = () => setKaSheetId(null);

  const saveKa = (data) => {
    setForm((prev) => ({
      ...prev,
      keyAchievements: prev.keyAchievements.map((k) =>
        k.id === kaSheetId ? { ...k, ...data } : k,
      ),
    }));
    closeKa();
  };

  const deleteKa = (id) =>
    setForm((prev) => ({
      ...prev,
      keyAchievements: prev.keyAchievements.filter((k) => k.id !== id),
    }));

  const onKaDragEnd = (result) => {
    if (!result.destination) return;
    setForm((prev) => {
      const items = Array.from(prev.keyAchievements);
      const [moved] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, moved);
      return { ...prev, keyAchievements: items };
    });
  };

  const editingKa = kaSheetId
    ? form.keyAchievements.find((k) => k.id === kaSheetId)
    : null;

  return (
    <div className='fixed inset-0 z-[60] flex flex-col bg-grayscale-gray00'>
      <div className='relative shrink-0 h-[54px] px-4 py-3 flex items-center justify-between border-b border-grayscale-gray02'>
        <CloseButton onClick={onClose} />
        <span className='flex-1 truncate px-2 text-center typo-b16Strong text-text-primary'>
          {t('Experience (Paid or Formal Work)', '경력 (유급 또는 정규 업무)')}
        </span>
        <div className='size-6' />
      </div>

      <div className='flex-1 overflow-y-auto px-4 pt-[30px] pb-[160px] flex flex-col gap-4'>
        {/* Company */}
        <Input
          ref={companyRef}
          label={`${t('Company or organization', '회사 또는 기관')}*`}
          placeholder={t('e.g. Google', '예: Google')}
          value={form.company}
          onChange={(e) => setField('company', e.target.value)}
          state={errors.company && 'failed'}
          message={errors.company}
          hideCount
        />

        {/* Job title */}
        <Input
          label={`${t('Job title', '직무명')}*`}
          placeholder={t('e.g. Visual Designer', '예: 비주얼 디자이너')}
          value={form.jobTitle}
          onChange={(e) => setField('jobTitle', e.target.value)}
          state={errors.jobTitle && 'failed'}
          message={errors.jobTitle}
          hideCount
        />

        {/* Employment type */}
        <div ref={employmentRef} className='relative'>
          <div onClick={() => setOpenEmployment((v) => !v)}>
            <Input
              label={`${t('Employment type', '고용 형태')}*`}
              placeholder={t('e.g. Full-time', '예: 정규직')}
              value={form.employmentType}
              readOnly
              hideCount
              state={errors.employmentType && 'failed'}
              message={errors.employmentType}
              rightIcon={
                openEmployment ? <ArrowTopIcon /> : <ArrowBottomIcon />
              }
            />
          </div>
          {openEmployment && (
            <div className='absolute top-[85px] left-0 z-20 w-full'>
              <EmploymentTypeList
                onSelect={(t) => {
                  setField('employmentType', t);
                  setOpenEmployment(false);
                }}
              />
            </div>
          )}
        </div>

        {/* Employment period */}
        <Input
          label={`${t('Employment period', '근무 기간')}*`}
          placeholder={t('e.g. 2023.03 – 2025.04', '예: 2023.03 – 2025.04')}
          value={form.employmentPeriod}
          onChange={(e) => setField('employmentPeriod', e.target.value)}
          state={errors.employmentPeriod && 'failed'}
          message={errors.employmentPeriod}
          hideCount
        />

        {/* Location */}
        <LocationInput
          value={form.location}
          focused={locationFocused}
          state={getInputState(locationFocused)}
          iconState={getIconState(form.location, locationFocused)}
          onChange={(e) => setField('location', e.target.value)}
          onFocus={() => setLocationFocused(true)}
          onBlur={() => setLocationFocused(false)}
          onSelect={(item) => {
            setField(
              'location',
              `${item.name}${item.country ? `, ${item.country}` : ''}`,
            );
            setLocationFocused(false);
          }}
          onManualEnter={(value) => {
            setField('location', value);
            setLocationFocused(false);
          }}
          isResume
        />

        {/* Location type */}
        <div ref={locationTypeRef} className='relative'>
          <div onClick={() => setOpenLocationType((v) => !v)}>
            <Input
              label={t('Location type', '근무 형태')}
              placeholder={t('e.g. On-site', '예: 출근 근무')}
              value={form.locationType}
              readOnly
              hideCount
              rightIcon={
                openLocationType ? <ArrowTopIcon /> : <ArrowBottomIcon />
              }
            />
          </div>
          {openLocationType && (
            <div className='absolute top-[85px] left-0 z-20 w-full'>
              <LocationTypeList
                onSelect={(t) => {
                  setField('locationType', t);
                  setOpenLocationType(false);
                }}
              />
            </div>
          )}
        </div>

        {/* Experience headline */}
        <div className='flex flex-col gap-2'>
          <span className='flex flex-row gap-[2px] text-grayscale-gray09'>
            {t('Experience headline', '경력 한 줄 소개')}
            <span className='relative'>
              <InfoIcon
                className='w-5 h-5'
                onClick={() => setShowInfo((v) => !v)}
              />
              {showInfo && (
                <div className='absolute -top-1.5 left-[30px] z-50'>
                  <ToolTip
                    label='Summarize the work in a few words'
                    labelKr='몇 단어로 업무를 요약해 주세요'
                    position='left'
                    align='top'
                    state='accent'
                    line='two'
                    className='w-[185px]'
                  />
                </div>
              )}
            </span>
          </span>
          <Input
            label=''
            placeholder={t(
              'e.g. Product Detail Page Redesign',
              '예: 제품 상세 페이지 리디자인',
            )}
            value={form.headline}
            onChange={(e) => setField('headline', e.target.value)}
            hideCount
          />
        </div>

        {/* Key Achievement */}
        <div className='mt-2'>
          {form.keyAchievements.length === 0 ? (
            <button
              type='button'
              onClick={addKaRow}
              className='flex items-center gap-1 h-11 px-4 rounded-[8px] bg-grayscale-gray01 text-text-placeholder transition-colors active:bg-grayscale-gray02'
            >
              <PlusIcon width={20} height={20} />
              <span className='typo-b14Strong'>
                {t('Add Key Achievement', '핵심 성과 추가')}
              </span>
            </button>
          ) : (
            <div className='flex flex-col gap-3 rounded-[8px] border border-green-light02 bg-green-light01/50 p-3'>
              <div className='flex items-center gap-1.5 text-text-primary'>
                <TrophyIcon width={20} height={20} />
                <span className='typo-b16Strong'>
                  {t('Key Achievement', '핵심 성과')}
                </span>
              </div>

              <DragDropContext onDragEnd={onKaDragEnd}>
                <Droppable droppableId='key-achievements' direction='vertical'>
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className='flex flex-col gap-3'
                    >
                      {form.keyAchievements.map((ka, index) => (
                        <Draggable
                          key={ka.id}
                          draggableId={String(ka.id)}
                          index={index}
                        >
                          {(prov) => (
                            <div
                              ref={prov.innerRef}
                              {...prov.draggableProps}
                              className='flex w-full'
                            >
                              {/* Drag handle */}
                              <span
                                {...prov.dragHandleProps}
                                className='shrink-0 flex items-center cursor-grab active:cursor-grabbing text-grayscale-gray04 py-[14px] w-[20px]'
                              >
                                <DragAndDropIcon />
                              </span>

                              <div className='flex-1 min-w-0 flex items-center rounded-[8px] border border-grayscale-gray02 bg-grayscale-gray00'>
                                {/* 요약 텍스트 */}
                                <button
                                  type='button'
                                  onClick={() => openKaEdit(ka.id)}
                                  className='flex-1 min-w-0 flex flex-col items-start gap-1 text-left px-4 py-3'
                                >
                                  <p
                                    className={`truncate max-w-full typo-b14Strong ${
                                      ka.title?.trim()
                                        ? 'text-text-primary'
                                        : 'text-text-placeholder'
                                    }`}
                                  >
                                    {ka.title?.trim() ||
                                      t(
                                        KA_PLACEHOLDER,
                                        '자랑스러운 성과를 작성해 주세요.',
                                      )}
                                  </p>
                                  {ka.description?.trim() && (
                                    <p className='line-clamp-2 break-words max-w-full typo-b12 text-text-secondary'>
                                      {ka.description}
                                    </p>
                                  )}
                                </button>

                                <button
                                  type='button'
                                  onClick={() => deleteKa(ka.id)}
                                  className='shrink-0 self-stretch flex items-center px-3 text-grayscale-gray04'
                                  aria-label={t(
                                    'Delete achievement',
                                    '성과 삭제',
                                  )}
                                >
                                  <DeleteIcon width={20} height={20} />
                                </button>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>

              <BaseButton
                onClick={addKaRow}
                type='secondary'
                icon={<PlusIcon width={20} height={20} />}
                bgclass='w-fit pl-2 pr-3 py-2.5'
              >
                {t('Add one more Achievement', '성과 추가')}
              </BaseButton>
            </div>
          )}
        </div>
      </div>

      <div className='shrink-0 p-4 border-t border-grayscale-gray02'>
        <button
          type='button'
          onClick={handleAdd}
          className='w-full h-[52px] rounded-[12px] bg-[#1DC37B] text-[#F9F7F4] text-[16px] font-medium transition-colors active:bg-[#009E4F]'
        >
          {t('Add', '추가')}
        </button>
      </div>

      {editingKa && (
        <MobileKeyAchievementBottomSheet
          initialData={editingKa}
          onClose={closeKa}
          onSave={saveKa}
        />
      )}
    </div>
  );
};

export default MobileExperienceFullPage;
